import { IABProduct } from "../../lib/payment"
import { IAPProduct } from "../../lib/payment"
import { z } from "zod/v4"
import type { DocumentSnapshotLike } from "../../firestore"
import { SubscriptionId, SubscriptionPlanId, SubscriptionPlanIdSchema, SubscriptionProductId, SubscriptionProductIdSchema, UserId, UserIdSchema } from "../../id"
import { CreatedAt, UpdatedAt } from "../system"
import { PlatformStoreSchema } from "../platform"
import { SubscriptionKindNormal, SubscriptionKindPremium, SubscriptionKindPremiumPlus } from "./subscription-kind"

export const IABSubscriptionReclaim = z.object({
  token: z.string(),
  product: IABProduct,
})

export type IABSubscriptionReclaim = z.infer<typeof IABSubscriptionReclaim>

export const IAPSubscriptionReclaim = z.object({
  token: z.string(),
  product: IAPProduct,
})

export type IAPSubscriptionReclaim = z.infer<typeof IAPSubscriptionReclaim>

export const SubscriptionPlanStatus = z.union([
  z.literal("public"),
  z.literal("hidden")
])

export type SubscriptionPlanStatus = z.infer<typeof SubscriptionPlanStatus>

export const SubscriptionProductDenormalized = z.object({
  id: SubscriptionProductIdSchema,
  product_id: z.string().describe("課金プラットフォーム上の商品IDです。iOSの場合はproductId、Androidの場合はbasePlanIdに対応します。"),
  price: z.number().describe("この商品の価格です。"),
  subscription_group_id: z.string().describe("この定期購入商品が属するグループのIDです。"),
})

export type SubscriptionProductDenormalized = z.infer<typeof SubscriptionProductDenormalized>

export const SubscriptionKindSchema = z.union([
  SubscriptionKindPremium,
  SubscriptionKindPremiumPlus,
])

export type SubscriptionKind = z.infer<typeof SubscriptionKindSchema>

export const SubscriptionPlanDocumentMeta = z.object({
  subscription_kind: SubscriptionKindSchema.describe("サブスクリプション種別です。"),
  ios: SubscriptionProductDenormalized.nullable().describe("非正規化されたiOS（App Store）用のサブスクリプション商品情報です。"),
  android: SubscriptionProductDenormalized.nullable().describe("非正規化されたAndroid（Google Play）用のサブスクリプション商品情報です。"),
  status: SubscriptionPlanStatus.describe("定期購入プランの公開状態を示します。"),
})

export type SubscriptionPlanDocumentMeta = z.infer<typeof SubscriptionPlanDocumentMeta>

export const SubscriptionPlanDocumentBody = SubscriptionPlanDocumentMeta.extend({
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type SubscriptionPlanDocumentBody = z.infer<typeof SubscriptionPlanDocumentBody>

export type SubscriptionPlanUpdatableFields = Pick<
  SubscriptionPlanDocumentBody,
  "status" | "ios" | "android" | "updated_at"
>

export type SubscriptionPlanDocument = SubscriptionPlanDocumentBody & {
  id: SubscriptionPlanId,
}

export class SubscriptionPlanDocuments {

  static toDocumentData(doc: SubscriptionPlanDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SubscriptionPlanDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SubscriptionPlanId(doc.id),
      subscription_kind: data["subscription_kind"],
      ios: data["ios"],
      android: data["android"],
      status: data["status"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const SubscriptionProductStatus = z.union([
  z.literal("public"),
  z.literal("hidden")
])

export type SubscriptionProductStatus = z.infer<typeof SubscriptionProductStatus>

export const SubscriptionProductDocumentMeta = z.object({
  plan_id: SubscriptionPlanIdSchema.describe("この商品が属する定期購入プランのIDです。"),
  product_id: z.string().describe("課金プラットフォーム上の商品IDです。iOSの場合はproductId、Androidの場合はbasePlanIdに対応します。"),
  subscription_group_id: z.string().describe("この定期購入商品が属するグループのIDです。"),
  price: z.number().describe("この商品の価格です。"),
  store: PlatformStoreSchema.describe("課金プラットフォーム（0: App Store, 1: Google Play）"),
  status: SubscriptionProductStatus.describe("定期購入商品の公開状態を示します。"),
})

export type SubscriptionProductDocumentMeta = z.infer<typeof SubscriptionProductDocumentMeta>

export const SubscriptionProductDocumentBody = SubscriptionProductDocumentMeta.extend({
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type SubscriptionProductDocumentBody = z.infer<typeof SubscriptionProductDocumentBody>

export type SubscriptionProductUpdatableFields = Pick<
  SubscriptionProductDocumentBody,
  "product_id" | "subscription_group_id" | "price" | "status" | "updated_at"
>

export type SubscriptionProductDocument = SubscriptionProductDocumentBody & {
  id: SubscriptionProductId,
}

export class SubscriptionProductDocuments {

  static toDocumentData(doc: SubscriptionProductDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SubscriptionProductDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SubscriptionProductId(doc.id),
      plan_id: SubscriptionPlanId(data["plan_id"]),
      store: data["store"],
      product_id: data["product_id"],
      subscription_group_id: data["subscription_group_id"],
      price: data["price"],
      status: data["status"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const CreateSubscriptionPlanRequest = z.object({
  subscriptionKind: SubscriptionKindSchema,
})

export type CreateSubscriptionPlanRequest = z.infer<typeof CreateSubscriptionPlanRequest>

export const UpdateSubscriptionPlanRequest = z.object({
  status: SubscriptionPlanStatus.optional(),
})

export type UpdateSubscriptionPlanRequest = z.infer<typeof UpdateSubscriptionPlanRequest>

export const CreateSubscriptionProductRequest = z.object({
  planId: SubscriptionPlanIdSchema,
  productId: z.string(),
  subscriptionGroupId: z.string(),
  price: z.number(),
  store: PlatformStoreSchema,
  status: SubscriptionProductStatus,
})

export type CreateSubscriptionProductRequest = z.infer<typeof CreateSubscriptionProductRequest>

export const UpdateSubscriptionProductRequest = z.object({
  productId: z.string().optional(),
  subscriptionGroupId: z.string().optional(),
  price: z.number().optional(),
  status: SubscriptionProductStatus.optional(),
})

export type UpdateSubscriptionProductRequest = z.infer<typeof UpdateSubscriptionProductRequest>

export const SubscriptionPlatform = z.enum(["iap", "iab", "admin"])
  .describe("サブスクリプションのプラットフォームを示します。iap: App Store, iab: Google Play, admin: 管理者付与")

export type SubscriptionPlatform = z.infer<typeof SubscriptionPlatform>

export const SubscriptionDocumentCommon = z.object({
  user_id: UserIdSchema,
  subscription_kind: SubscriptionKindSchema.describe("サブスクリプション種別です。"),
  status: z.union([
    z.literal("active"),
    z.literal("cancelled"),
    z.literal("expired"),
    z.literal("payment_failed"),
    z.literal("revoked"),
  ]).describe("サブスクリプションの状態。active: 有効、cancelled: 解約済み、expired: 期限切れ、payment_failed: 自動更新失敗、revoked: 管理者による取り消し"),
  start_at: z.number().describe("契約日時（Unix timestamp）"),
  expire_at: z.number().nullable().describe("有効期限（Unix timestamp）"),
})

export const SubscriptionDocumentMeta = z.intersection(SubscriptionDocumentCommon, z.union([
  z.object({
    platform: z.literal("iab").describe("Google Play (Android)"),
    token: z.string().describe("Google Play Purchase Token"),
    order_id: z.string().describe("Google Play Order ID"),
    package_name: z.string().describe("パッケージ名"),
    product_id: z.string().describe("サブスクリプション商品ID"),
  }),
  z.object({
    platform: z.literal("iap").describe("App Store (iOS)"),
    original_transaction_id: z.string().describe("オリジナルトランザクションID"),
    transaction_id: z.string().describe("個別トランザクションID"),
    bundle_id: z.string().describe("バンドルID"),
    product_id: z.string().describe("サブスクリプション商品ID"),
  }),
  z.object({
    platform: z.literal("admin").describe("管理者付与"),
    admin_user_id: UserIdSchema.optional().describe("付与した管理者のユーザーID"),
    note: z.string().optional().describe("管理者メモ"),
  }),
]))

export type SubscriptionDocumentMeta = z.infer<typeof SubscriptionDocumentMeta>

export const SubscriptionDocumentBody = z.intersection(SubscriptionDocumentMeta, z.object({
  created_at: CreatedAt,
  updated_at: UpdatedAt,
}))

export type SubscriptionDocumentBody = z.infer<typeof SubscriptionDocumentBody>

export type SubscriptionDocument = SubscriptionDocumentBody & {
  id: SubscriptionId,
}

export class SubscriptionDocuments {

  static toDocumentData(doc: SubscriptionDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SubscriptionDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const common = {
      id: SubscriptionId(doc.id),
      user_id: UserId(data["user_id"]),
      subscription_kind: data["subscription_kind"],
      status: data["status"],
      start_at: data["start_at"],
      expire_at: data["expire_at"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }

    // 旧データは数値（PlatformStore enum: 0=IAP, 1=IAB）で保存されている場合がある
    const rawPlatform = data["platform"]
    const platform = rawPlatform === 0 ? "iap" : rawPlatform === 1 ? "iab" : rawPlatform

    if (platform === "iab") {
      return {
        ...common,
        platform: "iab",
        token: data["token"],
        order_id: data["order_id"],
        package_name: data["package_name"],
        product_id: data["product_id"],
      }
    } else if (platform === "iap") {
      return {
        ...common,
        platform: "iap",
        original_transaction_id: data["original_transaction_id"],
        transaction_id: data["transaction_id"],
        bundle_id: data["bundle_id"],
        product_id: data["product_id"],
      }
    } else if (platform === "admin") {
      return {
        ...common,
        platform: "admin",
        admin_user_id: data["admin_user_id"] ? UserId(data["admin_user_id"]) : undefined,
        note: data["note"],
      }
    }

    throw new Error(`Unknown platform: ${platform}`)
  }
}

export const GrantUserSubscriptionByAdminRequest = z.object({
  subscriptionKind: z.union([
    SubscriptionKindNormal,
    SubscriptionKindPremium,
    SubscriptionKindPremiumPlus,
  ]),
})

export type GrantUserSubscriptionByAdminRequest = z.infer<typeof GrantUserSubscriptionByAdminRequest>
